import { isValidElement, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Printer, X } from 'lucide-react';
import { Button } from '@/ui/base/button';
import { PrintModeProvider } from './PrintMode';
import { PaginaPropria, Sheet } from './PrintBlocks';
import { PrintCover, Sumario } from './PrintCover';
import { DOC_VERSION, buildPrintFileName } from './printDoc';
import { aguardarRecursos, alturaUtilDaFolha, medirBloco } from './medir';
import {
  FOLHAS_ANTES_DAS_SECOES,
  paginar,
  paginasDoSumario,
  totalDeFolhas,
  type Paginacao,
  type Peca,
} from './paginar';
import { type SecaoDoDocumento } from './secoesDoDocumento';
import './impressao.css';

/** Classe do `<body>` durante a impressão: esconde tudo o que não é o documento. */
export const CLASSE_DE_IMPRESSAO = 'ds-print-active';

/** Classes de tema do `<html>` que o documento desliga: ele é sempre impresso no claro. */
const CLASSES_DE_TEMA = ['dark', 'a11y-high-contrast'];

/**
 * Margem zero da folha, só enquanto o documento está aberto. Fica fora de
 * impressao.css porque `@page` não se limita a um trecho da página: no arquivo,
 * valeria para toda impressão do sistema depois da primeira visita a esta tela,
 * e as outras (index.css) têm margem.
 */
const PAGINA_SEM_MARGEM = '@page { size: A4; margin: 0; }';

/**
 * Folga, em px, tirada da altura útil medida. Cobre o arredondamento entre a
 * medida da tela e o motor de impressão (1 mm = 3,78 px).
 */
const FOLGA_DE_MEDIDA = 2;

type Estado =
  | { fase: 'medindo' }
  | { fase: 'pronto'; paginacao: Paginacao; alturas: number[][] }
  | { fase: 'erro' };

interface DocumentoImpressoProps {
  secoes: SecaoDoDocumento[];
  aoFechar: () => void;
  /** Chamado com o total de folhas (capa e sumário incluídos) depois de paginar. */
  aoPaginar?: (total: number) => void;
  /** Data de emissão impressa na capa e no nome do arquivo. */
  emissao?: Date;
}

/**
 * O documento impresso do Design System: sobreposição com as folhas A4 e a
 * barra "Imprimir / Salvar PDF".
 *
 * Paginação por medição. O navegador imprime com a largura do papel, não com a
 * da janela: na folha A4 (210 mm, 794 px) o ponto de quebra `md` do Tailwind
 * vale e o `lg` não, numa tela larga ou estreita. Por isso os blocos são
 * medidos num quadro (iframe) invisível com a largura da folha e as mesmas
 * folhas de estilo — as regras de mídia ali dão o mesmo resultado que no papel.
 * Medidas a altura útil de cada folha e a de cada bloco (depois das fontes e
 * imagens), `paginar` decide a folha de cada um, e só então as folhas são
 * montadas: o sumário e o rodapé "n/N" saem desse cálculo.
 *
 * A impressão é a do navegador: texto vivo, componentes reais e cores dos
 * tokens. Enquanto a sobreposição está aberta o tema claro vale para a página
 * inteira (os outros temas têm página própria na seção Cores) e o resto da
 * página fica inerte.
 */
export function DocumentoImpresso({
  secoes,
  aoFechar,
  aoPaginar,
  emissao,
}: DocumentoImpressoProps) {
  const [estado, setEstado] = useState<Estado>({ fase: 'medindo' });
  const [dataDeEmissao] = useState(() => emissao ?? new Date());
  const [corpoDoQuadro, setCorpoDoQuadro] = useState<HTMLElement | null>(null);
  const estilosDoQuadro = useRef<Promise<unknown>>(Promise.resolve());
  const sobreposicaoRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  const medidorRef = useRef<HTMLDivElement>(null);
  const idDoTitulo = useId();

  const abreFolha = useMemo(
    () => secoes.map((s) => s.blocos.map((b) => isValidElement(b) && b.type === PaginaPropria)),
    [secoes],
  );
  const tituloMaisLongo = useMemo(
    () => secoes.reduce((maior, s) => (s.label.length > maior.length ? s.label : maior), ''),
    [secoes],
  );

  // Tema claro, nome do arquivo, classe de impressão, inércia do resto e foco.
  useEffect(() => {
    const html = document.documentElement;
    const temas = CLASSES_DE_TEMA.filter((c) => html.classList.contains(c));
    html.classList.remove(...temas);
    const esquemaAnterior = html.style.colorScheme;
    html.style.colorScheme = 'light';

    const inertes = Array.from(document.body.children).filter(
      (el) => el !== sobreposicaoRef.current && !el.hasAttribute('inert'),
    );
    inertes.forEach((el) => el.setAttribute('inert', ''));

    const focoAnterior =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    tituloRef.current?.focus();

    const tituloDaPagina = document.title;
    const preparar = () => {
      document.title = buildPrintFileName(dataDeEmissao);
      document.body.classList.add(CLASSE_DE_IMPRESSAO);
    };
    const limpar = () => {
      document.title = tituloDaPagina;
      document.body.classList.remove(CLASSE_DE_IMPRESSAO);
    };
    window.addEventListener('beforeprint', preparar);
    window.addEventListener('afterprint', limpar);

    return () => {
      window.removeEventListener('beforeprint', preparar);
      window.removeEventListener('afterprint', limpar);
      limpar();
      inertes.forEach((el) => el.removeAttribute('inert'));
      html.classList.add(...temas);
      html.style.colorScheme = esquemaAnterior;
      focoAnterior?.focus();
    };
  }, [dataDeEmissao]);

  // Esc fecha, como em todo diálogo do sistema.
  useEffect(() => {
    const aoTeclar = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  /**
   * O quadro de medida recebe as mesmas folhas de estilo da página. Sem `src`,
   * o documento do quadro é o about:blank inicial, que existe já na inserção e
   * não é trocado depois: dá para escrever nele na hora.
   */
  const prepararQuadro = (quadro: HTMLIFrameElement) => {
    let doc = quadro.contentDocument;
    // O about:blank inicial está em modo de compatibilidade (quirks): ali a
    // altura da linha em volta de selo e ícone sai outra, e cada linha de
    // selo mede 4 px a menos que no papel. Reescrito com doctype, fica no
    // modo padrão, como a página.
    if (doc && doc.compatMode !== 'CSS1Compat') {
      doc.open();
      doc.write('<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>');
      doc.close();
      doc = quadro.contentDocument;
    }
    if (!doc?.body) return;
    doc.documentElement.lang = document.documentElement.lang;
    doc.documentElement.className = Array.from(document.documentElement.classList)
      .filter((c) => !CLASSES_DE_TEMA.includes(c))
      .join(' ');
    doc.documentElement.style.overflow = 'hidden';
    doc.body.style.margin = '0';
    const carregando: Promise<unknown>[] = [];
    document.head.querySelectorAll('link[rel="stylesheet"], style').forEach((origem) => {
      const copia = doc.importNode(origem, true);
      if (copia instanceof doc.defaultView!.HTMLLinkElement) {
        carregando.push(
          new Promise((resolver) => {
            copia.addEventListener('load', resolver, { once: true });
            copia.addEventListener('error', resolver, { once: true });
          }),
        );
      }
      doc.head.appendChild(copia);
    });
    estilosDoQuadro.current = Promise.all(carregando);
    setCorpoDoQuadro(doc.body);
  };

  // Medição e paginação, dentro do quadro.
  useEffect(() => {
    if (estado.fase !== 'medindo' || !corpoDoQuadro) return;
    let cancelado = false;

    (async () => {
      try {
        await estilosDoQuadro.current;
        const medidor = medidorRef.current;
        if (!medidor) throw new Error('Medidor ausente.');
        await aguardarRecursos(medidor);
        if (cancelado) return;

        const alturaUtil = (chave: string) => {
          const folha = medidor.querySelector<HTMLElement>(`[data-medir-folha="${chave}"]`);
          if (!folha) throw new Error(`Folha de medida ${chave} ausente.`);
          return alturaUtilDaFolha(folha) - FOLGA_DE_MEDIDA;
        };

        const medidas = secoes.map((secao, i) => ({
          capacidadePrimeira: alturaUtil(String(i)),
          blocos: secao.blocos.map((_, j) => {
            const elemento = medidor.querySelector<HTMLElement>(`[data-medir-bloco="${i}-${j}"]`);
            if (!elemento) throw new Error(`Bloco ${i}-${j} ausente.`);
            return { ...medirBloco(elemento), novaPagina: abreFolha[i]![j] };
          }),
        }));
        const paginacao = paginar(medidas, alturaUtil('continuacao'));

        if (import.meta.env.DEV) {
          for (const aviso of paginacao.avisos) {
            console.warn(
              `[documento impresso] "${secoes[aviso.secao]!.label}", bloco ${aviso.bloco + 1}: maior que a folha e sem corte seguro; cortado na altura útil.`,
            );
          }
        }
        if (!cancelado) {
          setEstado({
            fase: 'pronto',
            paginacao,
            alturas: medidas.map((m) => m.blocos.map((b) => b.altura)),
          });
        }
      } catch {
        if (!cancelado) setEstado({ fase: 'erro' });
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [estado.fase, corpoDoQuadro, secoes, abreFolha]);

  const total = estado.fase === 'pronto' ? totalDeFolhas(estado.paginacao) : 0;

  useEffect(() => {
    if (estado.fase === 'pronto') aoPaginar?.(total);
  }, [estado.fase, total, aoPaginar]);

  const imprimir = () => {
    document.title = buildPrintFileName(dataDeEmissao);
    document.body.classList.add(CLASSE_DE_IMPRESSAO);
    window.print();
  };

  const situacao =
    estado.fase === 'medindo'
      ? 'Montando as folhas A4…'
      : estado.fase === 'erro'
        ? 'Não foi possível montar o documento. Feche e abra de novo.'
        : `${total} páginas A4. Na janela de impressão, escolha "Salvar como PDF".`;

  const medidor = (
    <div ref={medidorRef} className="ds-print-medidor">
      {secoes.map((secao, i) => (
        <div key={secao.id} data-medir-folha={i}>
          <Sheet
            pagina={0}
            total={0}
            secao={{ numero: i + 1, titulo: secao.label, intro: secao.intro }}
          />
        </div>
      ))}
      <div data-medir-folha="continuacao">
        <Sheet
          pagina={0}
          total={0}
          secao={{ numero: secoes.length, titulo: tituloMaisLongo }}
          continuacao
        />
      </div>
      <div className="ds-sheet ds-sheet-medidor">
        <div className="ds-sheet-body">
          {secoes.map((secao, i) =>
            secao.blocos.map((bloco, j) => (
              <div key={`${secao.id}-${j}`} className="ds-bloco" data-medir-bloco={`${i}-${j}`}>
                {bloco}
              </div>
            )),
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div
      ref={sobreposicaoRef}
      className="ds-print-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idDoTitulo}
    >
      <style>{PAGINA_SEM_MARGEM}</style>
      <div className="ds-print-barra">
        <div className="min-w-0">
          <h2
            id={idDoTitulo}
            ref={tituloRef}
            tabIndex={-1}
            className="text-sm font-semibold text-foreground outline-hidden"
          >
            Documento do Design System · {DOC_VERSION}
          </h2>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {situacao}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={imprimir} disabled={estado.fase !== 'pronto'}>
            {estado.fase === 'medindo' ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" />
            ) : (
              <Printer className="h-4 w-4 mr-1.5" aria-hidden="true" />
            )}
            Imprimir / Salvar PDF
          </Button>
          <Button variant="outline" onClick={aoFechar}>
            <X className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Fechar
          </Button>
        </div>
      </div>

      {estado.fase === 'medindo' && (
        // Um invólucro só, com o portal ANTES do quadro: ao sair, o React tira o
        // conteúdo do portal enquanto o documento do quadro ainda existe.
        <div>
          {corpoDoQuadro &&
            createPortal(<PrintModeProvider>{medidor}</PrintModeProvider>, corpoDoQuadro)}
          <iframe
            className="ds-print-quadro"
            title="Medição das folhas"
            aria-hidden="true"
            tabIndex={-1}
            ref={(quadro) => {
              if (quadro && !corpoDoQuadro) prepararQuadro(quadro);
            }}
          />
        </div>
      )}

      <PrintModeProvider>
        {estado.fase === 'pronto' && (
          <div className="ds-print-folhas">
            <PrintCover totalSections={secoes.length} emissao={dataDeEmissao} />
            <Sumario
              pagina={FOLHAS_ANTES_DAS_SECOES}
              total={total}
              toc={secoes.map((secao, i) => ({
                index: i + 1,
                label: secao.label,
                intro: secao.intro,
                page: paginasDoSumario(estado.paginacao)[i]!,
              }))}
            />
            {estado.paginacao.paginas.map((pagina, k) => {
              const secao = secoes[pagina.secao]!;
              return (
                <Sheet
                  key={k}
                  pagina={k + FOLHAS_ANTES_DAS_SECOES + 1}
                  total={total}
                  secao={{ numero: pagina.secao + 1, titulo: secao.label, intro: secao.intro }}
                  continuacao={pagina.continuacao}
                >
                  {pagina.pecas.map((peca) => (
                    <PecaDoBloco
                      key={`${peca.bloco}-${peca.inicio}`}
                      peca={peca}
                      altura={estado.alturas[pagina.secao]![peca.bloco]!}
                    >
                      {secao.blocos[peca.bloco]}
                    </PecaDoBloco>
                  ))}
                </Sheet>
              );
            })}
          </div>
        )}
      </PrintModeProvider>
    </div>,
    document.body,
  );
}

/** Bloco inteiro, ou a janela de um pedaço dele quando o bloco é maior que a folha. */
function PecaDoBloco({
  peca,
  altura,
  children,
}: {
  peca: Peca;
  altura: number;
  children: ReactNode;
}) {
  const inteiro = peca.inicio === 0 && peca.fim >= altura - 0.5;
  if (inteiro) return <div className="ds-bloco">{children}</div>;
  return (
    <div className="ds-bloco-fatia" style={{ height: peca.fim - peca.inicio }}>
      <div className="ds-bloco" style={{ marginTop: -peca.inicio }}>
        {children}
      </div>
    </div>
  );
}
